/**
 * Entity Mapping Configuration
 *
 * Comprehensive entity mapping for CRM database.
 * Maps database entities to API methods, refresh priorities, and loading phases.
 * Used by both web and mobile apps for data management.
 *
 * @module @prosperia/shared/data
 */

/**
 * Entity configuration mapping
 * Each entity has:
 * - apiMethod: The API method name to fetch data
 * - refreshInterval: How often to refresh (in ms)
 * - priority: 'critical' | 'high' | 'medium' | 'low'
 * - phase: 'critical' | 'essential' | 'important' | 'niceToHave' | 'system'
 * - requiresCompanyId: Whether the entity requires company context
 * - description: Human-readable description
 */
export const ENTITY_MAPPING = {
  // Core Business Entities (Critical - Phase 1)
  companies: {
    apiMethod: 'getCompanies',
    refreshInterval: 30 * 60 * 1000, // 30 minutes
    priority: 'critical',
    phase: 'critical',
    requiresCompanyId: false,
    description: 'Company information and settings'
  },

  // Essential Business Data (Phase 2)
  clients: {
    apiMethod: 'getClients',
    refreshInterval: 10 * 60 * 1000, // 10 minutes
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Client information and contacts'
  },
  services: {
    apiMethod: 'getServices',
    refreshInterval: 15 * 60 * 1000, // 15 minutes
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Service catalog and pricing'
  },
  packages: {
    apiMethod: 'getPackages',
    refreshInterval: 15 * 60 * 1000, // 15 minutes
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Service packages and bundles'
  },
  service_categories: {
    apiMethod: 'getServiceCategories',
    refreshInterval: 20 * 60 * 1000, // 20 minutes
    priority: 'medium',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Service categorization'
  },

  // Job Management (Phase 2 - Essential for Dashboard)
  jobs: {
    apiMethod: 'getJobs',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Job management and tracking'
  },
  job_activity: {
    apiMethod: 'getJobActivity',
    refreshInterval: 2 * 60 * 1000, // 2 minutes
    priority: 'high',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Job activity logs and updates'
  },
  job_checklists: {
    apiMethod: 'getJobChecklists',
    refreshInterval: 10 * 60 * 1000, // 10 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Job checklists and tasks'
  },
  job_checklist_items: {
    apiMethod: 'getJobChecklistItems',
    refreshInterval: 10 * 60 * 1000, // 10 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Individual checklist items'
  },

  // Financial Management (Phase 2 - Essential for Dashboard)
  estimates: {
    apiMethod: 'getEstimates',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Project estimates and quotes'
  },
  invoices: {
    apiMethod: 'getInvoices',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Invoice management'
  },
  invoice_photos: {
    apiMethod: 'getInvoicePhotos',
    refreshInterval: 10 * 60 * 1000, // 10 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Invoice photos and attachments'
  },
  payments: {
    apiMethod: 'getPayments',
    refreshInterval: 3 * 60 * 1000, // 3 minutes
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Payment tracking and processing'
  },
  expenses: {
    apiMethod: 'getExpenses',
    refreshInterval: 10 * 60 * 1000, // 10 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Expense tracking and management'
  },
  expense_categories: {
    apiMethod: 'getExpenseCategories',
    refreshInterval: 30 * 60 * 1000, // 30 minutes
    priority: 'low',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Expense categorization'
  },
  recurring_expenses: {
    apiMethod: 'getRecurringExpenses',
    refreshInterval: 15 * 60 * 1000, // 15 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Recurring expense management'
  },

  // Team Management (Phase 3 - Important)
  employees: {
    apiMethod: 'getEmployees',
    refreshInterval: 15 * 60 * 1000, // 15 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Employee management and permissions'
  },
  employee_permissions: {
    apiMethod: 'getEmployeePermissions',
    refreshInterval: 20 * 60 * 1000, // 20 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Employee permission management'
  },

  // Communication & Messaging (Phase 4 - Nice to Have)
  messages: {
    apiMethod: 'getMessages',
    refreshInterval: 1 * 60 * 1000, // 1 minute
    priority: 'high',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Internal messaging system'
  },
  message_logs: {
    apiMethod: 'getMessageLogs',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Message delivery logs'
  },
  email_logs: {
    apiMethod: 'getEmailLogs',
    refreshInterval: 10 * 60 * 1000, // 10 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Email delivery tracking'
  },

  // Calendar & Scheduling (Phase 4 - Nice to Have)
  calendar_events: {
    apiMethod: 'getCalendarEvents',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Calendar and scheduling'
  },

  // Automation & Workflow (Phase 4 - Nice to Have)
  automations: {
    apiMethod: 'getAutomations',
    refreshInterval: 20 * 60 * 1000, // 20 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Workflow automation rules'
  },
  automation_templates: {
    apiMethod: 'getAutomationTemplates',
    refreshInterval: 60 * 60 * 1000, // 1 hour
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Automation templates'
  },
  automation_executions: {
    apiMethod: 'getAutomationExecutions',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Automation execution logs'
  },
  scheduled_automation_queue: {
    apiMethod: 'getScheduledAutomationQueue',
    refreshInterval: 2 * 60 * 1000, // 2 minutes
    priority: 'high',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Scheduled automation queue'
  },

  // Magic Links & Public Access (Phase 4 - Nice to Have)
  magic_links: {
    apiMethod: 'getMagicLinks',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Magic link management'
  },
  client_responses: {
    apiMethod: 'getClientResponses',
    refreshInterval: 2 * 60 * 1000, // 2 minutes
    priority: 'high',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Client responses to estimates/invoices'
  },

  // AI & RAG (Phase 4 - Essential for AI Assistant)
  ai_conversations: {
    apiMethod: 'getAiConversations',
    refreshInterval: 10 * 60 * 1000, // 10 minutes
    priority: 'medium',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'AI conversation history'
  },
  ai_messages: {
    apiMethod: 'getAiMessages',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'AI message content'
  },
  rag_documents: {
    apiMethod: 'getRagDocuments',
    refreshInterval: 30 * 60 * 1000, // 30 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'RAG document storage'
  },
  rag_chunks: {
    apiMethod: 'getRagChunks',
    refreshInterval: 30 * 60 * 1000, // 30 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'RAG document chunks'
  },

  // File Management (Phase 4 - Nice to Have)
  files: {
    apiMethod: 'getFiles',
    refreshInterval: 20 * 60 * 1000, // 20 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'File storage and management'
  },
  job_media: {
    apiMethod: 'getJobMedia',
    refreshInterval: 15 * 60 * 1000, // 15 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Job-related media files'
  },
  job_checklist_attachments: {
    apiMethod: 'getJobChecklistAttachments',
    refreshInterval: 20 * 60 * 1000, // 20 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Checklist attachment files'
  },
  expense_receipts: {
    apiMethod: 'getExpenseReceipts',
    refreshInterval: 20 * 60 * 1000, // 20 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: false, // Note: getExpenseReceipts takes expenseId, not companyId
    requiresEntityId: true, // Requires specific expense ID - cannot be bulk loaded
    description: 'Expense receipt images (per expense, not bulk loadable)'
  },

  // Task Management (Phase 4 - Nice to Have)
  tasks: {
    apiMethod: 'getTasks',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Task management system'
  },

  // Vendor Management (Phase 4 - Nice to Have)
  vendors: {
    apiMethod: 'getVendors',
    refreshInterval: 30 * 60 * 1000, // 30 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'Vendor and supplier management'
  },

  // System & Configuration (Phase 2 - Essential for UI)
  company_branding: {
    apiMethod: 'getCompanyBranding',
    refreshInterval: 60 * 60 * 1000, // 1 hour
    priority: 'high',
    phase: 'essential',
    requiresCompanyId: true,
    description: 'Company branding settings'
  },
  user_dashboard_preferences: {
    apiMethod: 'getUserDashboardPreferences',
    refreshInterval: 30 * 60 * 1000, // 30 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: false,
    description: 'User dashboard customization'
  },
  user_toolbelt_preferences: {
    apiMethod: 'getUserToolbeltPreferences',
    refreshInterval: 30 * 60 * 1000, // 30 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: false,
    description: 'User toolbelt customization'
  },
  user_access_cache: {
    apiMethod: 'getUserAccessCache',
    refreshInterval: 5 * 60 * 1000, // 5 minutes - faster refresh for permissions
    priority: 'critical', // Critical for app functionality
    phase: 'essential', // Load early to prevent permission-related delays
    requiresCompanyId: false,
    description: 'User access permissions cache'
  },

  // Audit & Logging (Phase 4 - Nice to Have)
  audit_logs: {
    apiMethod: 'getAuditLogs',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'low',
    phase: 'niceToHave',
    requiresCompanyId: true,
    description: 'System audit logs'
  },

  // Webhooks & Integration (Phase 4 - Nice to Have)
  webhook_events: {
    apiMethod: 'getWebhookEvents',
    refreshInterval: 2 * 60 * 1000, // 2 minutes
    priority: 'medium',
    phase: 'niceToHave',
    requiresCompanyId: false,
    description: 'Webhook event processing'
  },

  // Computed Data Views (Phase 4 - Computed)
  job_financial_summary: {
    apiMethod: 'getJobFinancialSummary',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Job financial summary computed data',
    isComputed: true
  },
  job_costs_by_category: {
    apiMethod: 'getJobCostsByCategory',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Job costs grouped by category',
    isComputed: true
  },
  estimate_budget_by_category: {
    // No apiMethod: there is no backing data-layer method (the former
    // getEstimateBudgetByCategory was removed). This view is served purely
    // through the computed-data path (useComputedData), so fetchEntity's
    // apiMethod resolution never applies to it.
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Estimate budget grouped by category',
    isComputed: true
  },
  job_checklist_complete: {
    apiMethod: 'getChecklistByJobId',
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    priority: 'medium',
    phase: 'important',
    requiresCompanyId: true,
    description: 'Complete checklist data with items and attachments',
    isComputed: true
  },

  // Cache Management (System)
  cache_invalidation: {
    apiMethod: 'getCacheInvalidation',
    refreshInterval: 1 * 60 * 1000, // 1 minute
    priority: 'high',
    phase: 'system',
    requiresCompanyId: false,
    description: 'Cache invalidation tracking'
  }
}

/**
 * Entity groups for easier management
 */
export const ENTITY_GROUPS = {
  // Core business data
  core: ['companies', 'clients', 'services', 'packages', 'service_categories'],

  // Job management
  jobs: ['jobs', 'job_activity', 'job_checklists', 'job_checklist_items', 'job_media', 'job_checklist_attachments'],

  // Financial management
  financial: ['estimates', 'invoices', 'payments', 'expenses', 'expense_categories', 'recurring_expenses', 'expense_receipts'],

  // Team management
  team: ['employees', 'employee_permissions'],

  // Communication
  communication: ['messages', 'message_logs', 'email_logs', 'calendar_events'],

  // Automation
  automation: ['automations', 'automation_templates', 'automation_executions', 'scheduled_automation_queue'],

  // Public access
  public: ['magic_links', 'client_responses'],

  // AI & RAG
  ai: ['ai_conversations', 'ai_messages', 'rag_documents', 'rag_chunks'],

  // File management
  files: ['files', 'job_media', 'job_checklist_attachments', 'expense_receipts'],

  // Task management
  tasks: ['tasks'],

  // Vendor management
  vendors: ['vendors'],

  // System & configuration
  system: ['company_branding', 'user_dashboard_preferences', 'user_toolbelt_preferences', 'cache_invalidation'],

  // Audit & logging
  audit: ['audit_logs'],

  // Integration
  integration: ['webhook_events']
}

/**
 * Simplified 2-phase loading system
 */
export const LOADING_PHASES = {
  essential: [
    // Core business entities needed for app to function
    'user_access_cache', 'services', 'packages', 'service_categories',
    'job_activity',
    'company_branding', 'vendors',
    'job_checklists', 'job_checklist_items',
    'job_media', 'job_checklist_attachments', 'tasks',
    'ai_conversations'
    // NOTE: expense_receipts removed - getExpenseReceipts expects expenseId, not companyId
    // NOTE: calendar_events migrated to per-entity cache (lib/cache/calendarEvents.js)
    // NOTE: clients migrated to per-entity cache (lib/cache/clients.js)
    // NOTE: estimates migrated to per-entity cache (lib/cache/estimates.js)
    // NOTE: invoices migrated to per-entity cache (lib/cache/invoices.js)
    // NOTE: jobs migrated to per-entity cache (lib/cache/jobs.js); job_activity
    // still lives here because the per-job realtime subscription invalidates
    // ['job_activity', jobId] (see RealtimeManager.subscribeToJobDetails).
    // NOTE: companies migrated to per-entity cache (lib/cache/companies.js).
    // NOTE: payments migrated to per-entity cache (lib/cache/payments.js).
    // NOTE: expenses migrated to per-entity cache (lib/cache/expenses.js).
    // NOTE: employees migrated to per-entity cache (lib/cache/employees.js).
    // NOTE: expense_categories migrated (lib/cache/expenseCategories.js).
    // NOTE: recurring_expenses migrated (lib/cache/recurringExpenses.js).
  ],
  background: [
    // Optional data that can load in background
    'messages', 'automations', 'magic_links',
    'files', 'audit_logs', 'webhook_events', 'cache_invalidation'
  ]
}

/**
 * Role-based data access patterns
 */
export const ROLE_DATA_PATTERNS = {
  owner: [
    'services', 'packages',
    'automations', 'audit_logs', 'company_branding'
    // calendar_events, clients, companies, estimates, invoices, jobs, payments, expenses, employees migrated to per-entity cache
  ],
  manager: [
    'services', 'packages',
    'messages', 'tasks'
    // calendar_events, clients, companies, estimates, invoices, jobs, payments, expenses, employees migrated to per-entity cache
  ],
  employee: [
    'messages', 'tasks', 'files'
    // calendar_events, estimates, invoices, jobs migrated to per-entity cache
  ],
  viewer: [
    'messages'
    // calendar_events, clients, estimates, invoices, jobs migrated to per-entity cache
  ]
}

/**
 * Time-based data patterns
 */
export const TIME_DATA_PATTERNS = {
  // calendar_events, clients, estimates, invoices, jobs are on per-entity
  // caches and load via their own useQuery instances; do not list them here.
  morning: ['tasks'],
  afternoon: ['payments', 'messages', 'job_activity'],
  evening: ['reports', 'audit_logs', 'expenses', 'automations', 'ai_conversations']
}

/**
 * Seasonal data patterns
 */
export const SEASONAL_DATA_PATTERNS = {
  // calendar_events, clients, estimates, invoices, jobs are on per-entity
  // caches and load via their own useQuery instances; do not list them here.
  spring: ['services'], // Planning season
  summer: ['payments', 'job_activity'], // Execution season
  fall: ['payments', 'expenses', 'audit_logs'], // Collection season
  winter: ['reports', 'automations', 'ai_conversations', 'company_branding'] // Review season
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get configuration for a specific entity
 * @param {string} entityName - Name of the entity
 * @returns {Object|null} Entity configuration or null if not found
 */
export const getEntityConfig = (entityName) => {
  return ENTITY_MAPPING[entityName] || null
}

/**
 * Get entities by loading phase
 * @param {'essential'|'background'} phase - Loading phase
 * @returns {string[]} Array of entity names
 */
export const getEntitiesByPhase = (phase) => {
  return LOADING_PHASES[phase] || []
}

/**
 * Get entities by user role
 * @param {'owner'|'manager'|'employee'|'viewer'} role - User role
 * @returns {string[]} Array of entity names
 */
export const getEntitiesByRole = (role) => {
  return ROLE_DATA_PATTERNS[role] || []
}

/**
 * Get entities by time of day
 * @param {'morning'|'afternoon'|'evening'} timeOfDay - Time of day
 * @returns {string[]} Array of entity names
 */
export const getEntitiesByTime = (timeOfDay) => {
  return TIME_DATA_PATTERNS[timeOfDay] || []
}

/**
 * Get entities by season
 * @param {'spring'|'summer'|'fall'|'winter'} season - Season
 * @returns {string[]} Array of entity names
 */
export const getEntitiesBySeason = (season) => {
  return SEASONAL_DATA_PATTERNS[season] || []
}

/**
 * Get entities by group
 * @param {string} group - Group name
 * @returns {string[]} Array of entity names
 */
export const getEntitiesByGroup = (group) => {
  return ENTITY_GROUPS[group] || []
}

/**
 * Get all entity names
 * @returns {string[]} Array of all entity names
 */
export const getAllEntityNames = () => {
  return Object.keys(ENTITY_MAPPING)
}

/**
 * Get high priority entities
 * @returns {string[]} Array of high priority entity names
 */
export const getHighPriorityEntities = () => {
  return Object.entries(ENTITY_MAPPING)
    .filter(([, config]) => config.priority === 'high')
    .map(([name]) => name)
}

/**
 * Get critical entities
 * @returns {string[]} Array of critical entity names
 */
export const getCriticalEntities = () => {
  return Object.entries(ENTITY_MAPPING)
    .filter(([, config]) => config.priority === 'critical')
    .map(([name]) => name)
}

/**
 * Get entities that require company ID
 * @returns {string[]} Array of entity names requiring company ID
 */
export const getEntitiesRequiringCompanyId = () => {
  return Object.entries(ENTITY_MAPPING)
    .filter(([, config]) => config.requiresCompanyId)
    .map(([name]) => name)
}

/**
 * Get entities that don't require company ID
 * @returns {string[]} Array of entity names not requiring company ID
 */
export const getEntitiesNotRequiringCompanyId = () => {
  return Object.entries(ENTITY_MAPPING)
    .filter(([, config]) => !config.requiresCompanyId)
    .map(([name]) => name)
}
